import { useContext } from "react";

import { observer } from "mobx-react-lite";

import { InlineNode } from "@/components/expression/InlineNode";
import * as Node from "@/components/expression/utils/layout";
import { useNodeStyle } from "@/components/expression/utils/NodeStyleProvider";
import { useNodeHover } from "@/components/expression/utils/useNodeHover";
import { twMerge } from "@/utils/twMerge";

import type { ExpressionComponent } from "./types";

/*
  We use css <table /> style here because of the element vertical alignment problem

  Without css <table /> you have this alignment:

                      2
              –––––––––––––––––
                        4
          1 +     –––––––––––––
              3 +         6
                  5 + –––––––––
                          7


  With css <table /> you can achieve this:

                      2
          1 + –––––––––––––––––
                        4
              3 + –––––––––––––
                          6
                  5 + –––––––––
                          7

  Note that in `display: inline-block;` element the baseline is aligned with the _bottom_ text line,
  but in `display: inline-table;` it's aligned with the _top_ text line.
*/

export const FractionNode: ExpressionComponent<"operator"> = observer(function FractionNode({
  node,
  className,
}) {
  const isContextSelected = useContext(Node.SelectedContext);
  const {
    parameters: [numerator, denominator],
    doc,
  } = node;
  const isActive = doc.selection.isActive(node);
  const isSelected = isContextSelected || isActive || doc.selection.isSelected(node);
  const { handlerProps } = useNodeHover(node);
  const { view, text, size, debugOutlineNodes } = useNodeStyle();

  return (
    <Node.SelectedContext.Provider value={isSelected}>
      <div
        {...handlerProps}
        className={twMerge(
          "border border-transparent inline rounded-5 px-0.5 flex-col min-w-[20px] -mx-0.5",
          view === "text" && "rounded-none border-none min-w-3 p-0 m-0",
          view === "text" &&
            (size === "xxs" || size === "xxxs") &&
            "-translate-y-[5px] mt-[5px] -mb-[5px]",
          debugOutlineNodes && "border p-1",
          className,
        )}
      >
        <Node.Container
          node={node}
          className={twMerge(
            "inline-block rounded-5 p-0",
            isSelected && "bg-selected-bg text-selected-fg",
          )}
        >
          {numerator && <InlineNode node={numerator} className="pb-0.5" withFillers />}
          {/* TODO: a11y with hidden */}
          <div className="inline-table w-full">
            <div
              className={twMerge(
                "h-[1px] leading-[1px] mx-0.5 bg-document-text before:content-zero-space rounded-6 relative z-10",
                size === "sm" && "h-[1.5px] leading-[1.5px]",
                text === "parameter" && "border-function-parameter",
              )}
            >
              <span className="hidden">/</span>
            </div>
            {denominator && <InlineNode node={denominator} className="pt-0.5" withFillers />}
          </div>
        </Node.Container>
      </div>
    </Node.SelectedContext.Provider>
  );
});
